/**
 * MaterialCombobox — Editable autocomplete for inventory materials.
 * Iter 96 (Fix Package 02/03 Bölüm C): Satın Alma → Yeni PO → Malzeme Adı.
 *
 * Kullanıcı elle yazabilir, mevcut Inventory materials filtrelenir (Türkçe safe,
 * whitespace-normalized). Tam eşleşme yoksa "+ Yeni Envanter Ekle" tetikleyicisi
 * gösterilir. onCreate callback'ine yazılan text iletilir; parent inline modal ile
 * yeni material oluşturup material_id ile onChange'i tekrar tetikler.
 *
 * Props:
 *   value        string — material_id (mevcut seçim)
 *   materials    array — [{id, name, unit, code, is_deleted}]
 *   onChange     (materialId) => void
 *   onCreate     (typedName) => void
 *   placeholder  string
 *   testid       string
 *   className    string
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus } from "lucide-react";

const norm = (v) => (v || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("tr");

export default function MaterialCombobox({
  value, materials = [], onChange, onCreate,
  placeholder = "Malzeme adı yazın...", testid, className = "",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);

  const activeMaterials = useMemo(
    () => (materials || []).filter(m => !m.is_deleted),
    [materials],
  );

  // Sync display when value changes externally (parent created a new material).
  useEffect(() => {
    if (!value) { setQuery(""); return; }
    const m = activeMaterials.find(x => x.id === value);
    if (m) setQuery(m.name || "");
  }, [value, activeMaterials]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const q = norm(query);
  const suggestions = useMemo(() => {
    if (!q) return activeMaterials.slice(0, 12);
    return activeMaterials.filter(m => norm(m.name).includes(q)).slice(0, 12);
  }, [q, activeMaterials]);

  const exactMatch = useMemo(() => {
    if (!q) return null;
    return activeMaterials.find(m => norm(m.name) === q) || null;
  }, [q, activeMaterials]);

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions[highlight]) { pick(suggestions[highlight]); }
      else if (query.trim() && onCreate) { setOpen(false); onCreate(query.trim()); }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  const pick = (m) => {
    setQuery(m.name || "");
    setOpen(false);
    onChange?.(m.id);
  };

  return (
    <div className={`relative ${className}`} ref={boxRef} data-testid={testid || "material-combobox"}>
      <div className="flex items-center gap-1.5 px-2 py-1 border border-slate-300 rounded bg-white">
        <Search size={12} className="text-slate-400 flex-shrink-0"/>
        <input
          value={query}
          onFocus={() => { setOpen(true); setHighlight(0); }}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
            // Kullanıcı manuel değiştirirse ve tam eşleşme kalmadıysa material_id'yi temizle.
            // Backend zaten boş id'yi reddeder — kullanıcı ya öneri seçecek ya da yeni envanter ekleyecek.
            const nm = norm(e.target.value);
            const match = activeMaterials.find(x => norm(x.name) === nm);
            if (match) { onChange?.(match.id); } else if (value) { onChange?.(""); }
          }}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="flex-1 text-xs outline-none bg-transparent"
          data-testid={`${testid || "material-combobox"}-input`}
          autoComplete="off"
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded shadow-lg" data-testid={`${testid || "material-combobox"}-list`}>
          {suggestions.length === 0 && !query.trim() && (
            <div className="p-3 text-xs text-slate-400 italic">Malzeme adı yazın...</div>
          )}
          {suggestions.length === 0 && query.trim() && (
            <div className="px-3 py-2">
              <div className="text-xs text-rose-700 font-medium mb-1.5">Bu malzeme envanterde bulunamadı.</div>
              <button
                type="button"
                onClick={() => { setOpen(false); onCreate?.(query.trim()); }}
                className="w-full flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold hover:brightness-110"
                data-testid={`${testid || "material-combobox"}-create`}
              >
                <Plus size={12}/> Yeni Envanter Ekle ({query.trim().slice(0, 30)}{query.trim().length > 30 ? "…" : ""})
              </button>
            </div>
          )}
          {suggestions.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => pick(m)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-1.5 text-xs border-b border-slate-100 last:border-0 ${i === highlight ? "bg-indigo-50" : "hover:bg-slate-50"}`}
              data-testid={`${testid || "material-combobox"}-opt-${m.id}`}
            >
              <div className="font-medium text-slate-800">{m.name}</div>
              <div className="text-[10px] text-slate-500 flex gap-2">
                <span className="font-mono">{m.code || "—"}</span>
                <span>·</span>
                <span>{m.unit || "kg"}</span>
                {m.category && (<><span>·</span><span>{m.category}</span></>)}
              </div>
            </button>
          ))}
          {suggestions.length > 0 && !exactMatch && query.trim() && (
            <button
              type="button"
              onClick={() => { setOpen(false); onCreate?.(query.trim()); }}
              className="w-full flex items-center justify-center gap-1.5 text-xs px-3 py-2 border-t border-slate-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold"
              data-testid={`${testid || "material-combobox"}-create`}
            >
              <Plus size={12}/> Yeni Envanter Ekle ({query.trim().slice(0, 30)}{query.trim().length > 30 ? "…" : ""})
            </button>
          )}
        </div>
      )}
    </div>
  );
}
